import {Client, Message} from 'paho-mqtt';

class PahoMqttClient {
    constructor() {
        this.client = null;
        this._config = null;          // last connect args, for reconnecting
        this._intentional = false;    // true after an explicit disconnect()
        this._reconnectTimer = null;
        this._attempts = 0;
    }

    connect(brokerUrl, clientId, options = {}) {
        // Close any previous client before replacing it, so an overlapping
        // reconnect/connect (which may use different clientIds and thus won't be
        // evicted by broker session-takeover) can't leave two live sockets. Detach
        // its onConnectionLost first so tearing it down here can't itself schedule
        // another reconnect.
        if (this.client) {
            try {
                this.client.onConnectionLost = () => {};
                this.client.disconnect();
            } catch (e) { /* already disconnected */ }
        }

        this._config = {brokerUrl, clientId, options};
        this._intentional = false;
        this._clearReconnectTimer();

        // Create a new MQTT client instance
        this.client = new Client(brokerUrl, clientId);
        // Identity guard: ignore any callback from a client that a later connect()
        // has already replaced (an aborted mid-handshake client's onFailure, or a
        // stale onConnectionLost) so it can't schedule a reconnect or clobber the
        // current client's state.
        const thisClient = this.client;

        // Reconnect on an unexpected drop (not on an intentional disconnect).
        this.client.onConnectionLost = (response) => {
            if (this.client !== thisClient) return;
            console.error('MQTT connection lost:', response.errorMessage);
            if (!this._intentional) this._scheduleReconnect();
        };

        // Re-attach the message handler on EVERY (re)connect. A reconnect builds a
        // NEW Client, so without this a reconnected stream silently receives nothing
        // (the "live location sometimes stale / frozen map" bug).
        if (options.onMessageArrived) {
            this.client.onMessageArrived = options.onMessageArrived;
        }

        const connectionOptions = {
            // Shorter keep-alive than paho's 60s default so a half-open/dead link is
            // detected (onConnectionLost) within ~one interval and we recover fast.
            keepAliveInterval: options.keepAliveInterval || 30,
            onSuccess: () => {
                if (this.client !== thisClient) return; // superseded by a newer connect()
                console.log('Connected to MQTT broker');
                this._attempts = 0;
                this._clearReconnectTimer();
                if (options.onSuccess) {
                    options.onSuccess(); // re-subscribes on (re)connect
                }
            },
            onFailure: (err) => {
                if (this.client !== thisClient) return; // superseded by a newer connect()
                console.error('MQTT connection failed:', err.errorMessage);
                if (options.onFailure) {
                    options.onFailure(err);
                }
                if (!this._intentional) this._scheduleReconnect();
            },
            useSSL: brokerUrl.startsWith('wss://'),
        };

        // Add username and password only if provided
        if (options.username) connectionOptions.userName = options.username;
        if (options.password) connectionOptions.password = options.password;

        // Connect to the broker
        this.client.connect(connectionOptions);
    }

    _scheduleReconnect() {
        if (this._intentional || this._reconnectTimer || !this._config) return;
        this._attempts += 1;
        // Exponential backoff (2s,4s,8s… capped 30s) + small jitter so many clients
        // dropped by one broker blip don't reconnect in lockstep (token-endpoint herd).
        const delay = Math.min(30000, 1000 * Math.pow(2, this._attempts)) + Math.floor(Math.random() * 1000);
        console.log(`MQTT reconnecting in ${delay}ms (attempt ${this._attempts})`);
        this._reconnectTimer = setTimeout(async () => {
            this._reconnectTimer = null;
            if (this._intentional || !this._config) return;
            const cfg = this._config; // capture; bail after the await if superseded
            const {brokerUrl, clientId, options} = cfg;
            // Refresh the JWT before reconnecting: the drop may have been caused by
            // token expiry, and reusing the stale token would loop forever.
            if (options.refreshToken) {
                try {
                    const token = await options.refreshToken();
                    if (token) options.username = token;
                } catch (e) {
                    console.error('MQTT: token refresh before reconnect failed', e);
                }
            }
            // Bail if a fresh connect() superseded this reconnect during the await
            // (e.g. route-away-and-back mounted a new instance): reconnecting with
            // the stale captured config would tear down the new client -> freeze.
            if (this._intentional || this._config !== cfg) return;
            this.connect(brokerUrl, clientId, options);
        }, delay);
    }

    _clearReconnectTimer() {
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
    }

    subscribe(topic) {
        if (this.client && this.client.isConnected()) {
            this.client.subscribe(topic);
        } else {
            console.error('MQTT client is not connected');
        }
    }

    unsubscribe(topic) {
        if (this.client && this.client.isConnected()) {
            this.client.unsubscribe(topic);
            console.log(`Unsubscribed to topic: ${topic}`);
        } else {
            console.error('MQTT client is not connected');
        }
    }

    publish(topic, message) {
        if (this.client && this.client.isConnected()) {
            const mqttMessage = new Message(message);
            mqttMessage.destinationName = topic;
            this.client.send(mqttMessage);
            console.log(`Published message: ${message} to topic: ${topic}`);
        } else {
            console.error('MQTT client is not connected');
        }
    }

    disconnect() {
        this._intentional = true;
        this._clearReconnectTimer();
        if (this.client) {
            try {
                this.client.disconnect();
            } catch (e) {
                // already disconnected
            }
            console.log('Disconnected from MQTT broker');
        }
    }
}

export default new PahoMqttClient();
